// client/src/utils/chapterStateUtils.ts
// Chapter state utilities with no-downgrade protection
import { pickTimestamp, pickChaptersArray, pickChaptersCount } from './cacheBootstrap';
import { useRef } from 'react';

interface ChapterSource {
  src: string;
  count: number;
  ts: string | null;
  data?: any[];
  contentHash?: string;
  confidence: number; // 0-1 confidence score based on data quality
}

interface ChapterSources {
  preview?: any[];
  fromDb?: any[];
  bootstrap?: any[];
  projectSummaryCount?: number;
  projectTitle?: string;
  updatedAt: {
    preview?: string;
    fromDb?: string;
    bootstrap?: string;
    projectSummary?: string;
  };
}

interface DerivedChapterState {
  chaptersCount: number;
  sourceUsed: string;
  allowOverwrite: boolean;
  projectTitle?: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

/**
 * Single source of truth for chapter state derivation with no-downgrade protection
 * Enhanced with tolerant timestamp checking and confidence scoring
 */
export function deriveChaptersState(sources: ChapterSources): DerivedChapterState {
  // ✅ TIMESTAMP SYNTHESIS: Generate fallback timestamps for missing ones
  const now = new Date().toISOString();

  // Create source array with comprehensive metadata using tolerant pickers
  const sourceCandidates: ChapterSource[] = [
    {
      src: 'preview',
      count: pickChaptersCount(sources.preview) || sources.preview?.length || 0,
      ts: pickTimestamp(sources.preview) || sources.updatedAt.preview || now,
      data: pickChaptersArray(sources.preview) || sources.preview,
      contentHash: sources.preview ? generateContentHash(sources.preview) : undefined,
      confidence: calculateSourceConfidence('preview', sources.preview, pickTimestamp(sources.preview) || sources.updatedAt.preview || now)
    },
    {
      src: 'fromDb',
      count: pickChaptersCount(sources.fromDb) || sources.fromDb?.length || 0,
      ts: pickTimestamp(sources.fromDb) || sources.updatedAt.fromDb || now,
      data: pickChaptersArray(sources.fromDb) || sources.fromDb,
      contentHash: sources.fromDb ? generateContentHash(sources.fromDb) : undefined,
      confidence: calculateSourceConfidence('fromDb', sources.fromDb, pickTimestamp(sources.fromDb) || sources.updatedAt.fromDb || now)
    },
    {
      src: 'bootstrap',
      count: pickChaptersCount(sources.bootstrap) || sources.bootstrap?.length || 0,
      ts: pickTimestamp(sources.bootstrap) || sources.updatedAt.bootstrap || now,
      data: pickChaptersArray(sources.bootstrap) || sources.bootstrap,
      contentHash: sources.bootstrap ? generateContentHash(sources.bootstrap) : undefined,
      confidence: calculateSourceConfidence('bootstrap', sources.bootstrap, pickTimestamp(sources.bootstrap) || sources.updatedAt.bootstrap || now)
    },
    {
      src: 'projectSummary',
      count: sources.projectSummaryCount ?? 0,
      ts: sources.updatedAt.projectSummary || now,
      data: undefined, // ProjectSummary doesn't have actual chapter data
      contentHash: undefined,
      confidence: calculateSourceConfidence('projectSummary', { count: sources.projectSummaryCount }, sources.updatedAt.projectSummary || now)
    },
  ];

  // RULE 1: Enhanced filtering - prioritize data presence over timestamp requirements
  const validSources = sourceCandidates.filter(source => {
    // If source has chapter data, keep it even with weak timestamp
    if (source.count > 0 && source.data && Array.isArray(source.data)) {
      if (!source.ts || source.ts === 'No timestamp') {
        source.confidence = Math.min(source.confidence, 0.3); // Reduce confidence but keep
      }
      return true;
    }

    // For metadata-only sources (like projectSummary), require timestamp
    if (!source.data && source.count > 0) {
      if (!source.ts || source.ts === 'No timestamp') {
        return false;
      }
      return true;
    }

    // Empty sources (count = 0) - keep only if they have valid timestamps (for potential future data)
    if (source.count === 0) {
      if (!source.ts || source.ts === 'No timestamp') {
        return false;
      }
      return true;
    }

    return true;
  });

  // RULE 2: Sort by count (descending), then by confidence, then by timestamp (newest first)
  validSources.sort((a, b) => {
    // First priority: higher count wins
    if (b.count !== a.count) {
      return b.count - a.count;
    }

    // Second priority: higher confidence wins (when counts are equal)
    if (Math.abs(b.confidence - a.confidence) > 0.1) {
      return b.confidence - a.confidence;
    }

    // Third priority: newer timestamp wins
    const aTime = new Date(a.ts || 0).getTime();
    const bTime = new Date(b.ts || 0).getTime();
    return bTime - aTime;
  });

  // RULE 3: Select best source with no-downgrade protection
  let selectedSource = validSources[0];
  let allowOverwrite = true;
  let confidence: 'high' | 'medium' | 'low' = 'high';
  let reasoning = '';

  if (!selectedSource) {
    // Fallback to any source with data, even without timestamp
    const fallbackSource = sourceCandidates.find(s => s.count > 0);
    if (fallbackSource) {
      selectedSource = fallbackSource;
      allowOverwrite = false; // Don't allow overwrites for fallback
      confidence = 'low';
      reasoning = 'Using fallback source without valid timestamp';
    } else {
      // No sources with data available
      selectedSource = sourceCandidates[0] || { src: 'none', count: 0, ts: null };
      allowOverwrite = false;
      confidence = 'low';
      reasoning = 'No sources with chapter data available';
    }
  } else {
    // RULE 4: Apply no-downgrade protection
    const hasZeroCount = selectedSource.count === 0;
    const hasValidTimestamp = selectedSource.ts && selectedSource.ts !== 'No timestamp';
    const isNewestSource = validSources.length === 1 ||
      validSources.every(s => new Date(selectedSource.ts || 0).getTime() >= new Date(s.ts || 0).getTime());

    if (hasZeroCount) {
      // Zero-count source selected - apply strict validation
      allowOverwrite = !!hasValidTimestamp && isNewestSource;
      confidence = allowOverwrite ? 'medium' : 'low';
      reasoning = allowOverwrite
        ? 'Zero-count source allowed (newest timestamp)'
        : 'Zero-count source blocked (protection against downgrade)';
    } else {
      // Non-zero source selected - generally safe
      allowOverwrite = true;
      confidence = 'high';
      reasoning = `Valid source with ${selectedSource.count} chapters`;
    }
  }

  // RULE 5: Extract projectTitle with fallback chain
  const projectTitle = sources.projectTitle ||
    extractTitleFromChapterData(selectedSource.data || []) ||
    extractTitleFromChapterData(sourceCandidates.find(s => s.data)?.data || []) ||
    undefined;

  const result: DerivedChapterState = {
    chaptersCount: selectedSource.count,
    sourceUsed: selectedSource.src,
    allowOverwrite,
    projectTitle,
    confidence,
    reasoning
  };

  return result;
}

/**
 * Generate a simple content hash for chapter data to detect meaningful changes
 */
function generateContentHash(chapters: any[]): string {
  if (!chapters || chapters.length === 0) return '';

  // Create hash based on chapter structure (id, title, summary)
  const hashInput = chapters.map(ch => ({
    id: ch.id,
    title: ch.title,
    summary: ch.summary?.substring(0, 100) // First 100 chars for performance
  }));

  return btoa(JSON.stringify(hashInput)).substring(0, 16);
}

/**
 * Extract project title from chapter data if available
 */
function extractTitleFromChapterData(chapters: any[]): string | undefined {
  if (!chapters || chapters.length === 0) return undefined;

  // Look for title in first chapter or chapter metadata
  const firstChapter = chapters[0];
  if (firstChapter?.projectTitle) return firstChapter.projectTitle;
  if (firstChapter?.bookTitle) return firstChapter.bookTitle;

  return undefined;
}

/**
 * Validate that a chapter state change is allowed based on no-downgrade rules
 */
export function validateChapterStateChange(
  currentState: { chaptersCount: number; sourceUsed?: string },
  newState: DerivedChapterState,
  options: { force?: boolean } = {}
): { allowed: boolean; reason: string } {
  // Always allow if forced
  if (options.force) {
    return { allowed: true, reason: 'Force flag enabled' };
  }

  // Always allow if new state has more chapters
  if (newState.chaptersCount > currentState.chaptersCount) {
    return { allowed: true, reason: 'Chapter count increased' };
  }

  // Block if new state has fewer chapters unless explicitly allowed
  if (newState.chaptersCount < currentState.chaptersCount && !newState.allowOverwrite) {
    return {
      allowed: false,
      reason: `Downgrade blocked: ${currentState.chaptersCount} → ${newState.chaptersCount}`
    };
  }

  // Allow if counts are equal (no change in count)
  if (newState.chaptersCount === currentState.chaptersCount) {
    return { allowed: true, reason: 'Chapter count unchanged' };
  }

  // Allow if overwrite is explicitly permitted (newer timestamp + content difference)
  if (newState.allowOverwrite) {
    return { allowed: true, reason: 'Overwrite allowed by state derivation' };
  }

  // Default: block the change
  return {
    allowed: false,
    reason: 'Change blocked by no-downgrade protection'
  };
}

/**
 * Enhanced logging for chapter state transitions
 */
export function logChapterStateTransition(
  from: { chaptersCount: number; sourceUsed?: string },
  to: DerivedChapterState,
  allowed: boolean,
  reason: string
): void {
  const emoji = allowed ? '✅' : '❌';
  const direction = to.chaptersCount > from.chaptersCount ? '⬆️' :
                   to.chaptersCount < from.chaptersCount ? '⬇️' : '➡️';

}

/**
 * Calculate confidence score for a chapter source based on data quality
 */
function calculateSourceConfidence(srcType: string, data: any, timestamp: string | null): number {
  let confidence = 0.5; // Base confidence

  // Source type reliability (fromDb > preview > bootstrap > projectSummary)
  switch (srcType) {
    case 'fromDb':
      confidence += 0.3;
      break;
    case 'preview':
      confidence += 0.2;
      break;
    case 'bootstrap':
      confidence += 0.1;
      break;
    case 'projectSummary':
      confidence += 0.05;
      break;
  }

  // Timestamp quality bonus
  if (timestamp && timestamp !== 'No timestamp') {
    const age = Date.now() - new Date(timestamp).getTime();
    const ageInHours = age / (1000 * 60 * 60);

    if (ageInHours < 1) confidence += 0.3;      // Very fresh
    else if (ageInHours < 24) confidence += 0.2; // Fresh
    else if (ageInHours < 168) confidence += 0.1; // Recent (1 week)
    // Older timestamps get no bonus
  } else {
    confidence -= 0.2; // Penalty for missing timestamp
  }

  // Data quality bonus
  if (data) {
    const chaptersArray = pickChaptersArray(data);
    if (chaptersArray && Array.isArray(chaptersArray) && chaptersArray.length > 0) {
      confidence += 0.2; // Has actual chapter data

      // Check if chapters have proper structure
      const firstChapter = chaptersArray[0];
      if (firstChapter?.id && firstChapter?.title) {
        confidence += 0.1; // Well-structured chapter data
      }
    } else if (pickChaptersCount(data) > 0) {
      confidence += 0.1; // Has count metadata
    }
  }

  // Clamp to 0-1 range
  return Math.max(0, Math.min(1, confidence));
}